import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Box, IconButton } from '@material-ui/core';

import { RiCheckLine, RiFileCopyLine } from '@remixicon/react';

import { ChartBlock } from '@spotify/confidence-conversation-react';
import { useAlert } from '@spotify/confidence-core-react';
import { PulseV1PulseBlockType } from '@spotify/confidence-graphql';

import { useChatContext } from '../ChatThread/ChatContext';
import { MarkdownContent } from '../MarkdownContent';
import { InlineBlockRenderProvider } from '../MarkdownContent/InlineBlockContext';
import { stripPulseNumberMarkers } from '../MarkdownContent/inlineBlockMarkers';
import { FeedbackToolbar } from './FeedbackToolbar';
import {
  type InlineChartBlock,
  type InlineNumberBlock,
  type InlineTableBlock,
  type InlineTextBlock,
  blockToMarkdownTable,
  resolveInlineBlock,
  resolveInlineNumberBlock,
} from './inlineBlocks';

import styles from './AssistantMessage.module.css';

const renderBlockContent = (
  block: InlineTableBlock | InlineChartBlock | InlineTextBlock,
): ReactNode => {
  if (block.type === PulseV1PulseBlockType.PulseBlockTypeTable) {
    return <MarkdownContent>{blockToMarkdownTable(block)}</MarkdownContent>;
  }
  if (block.type === PulseV1PulseBlockType.PulseBlockTypeText) {
    return <MarkdownContent>{block.content}</MarkdownContent>;
  }
  return <ChartBlock content={block.spec} />;
};

interface AssistantMessageProps {
  content: string;
  eventName?: string;
  renderBlockActions?: (
    block: InlineTableBlock | InlineChartBlock | InlineTextBlock,
  ) => ReactNode;
  renderInlineNumber?: (
    block: InlineNumberBlock,
    value: ReactNode,
  ) => ReactNode;
}

export const AssistantMessage = ({
  content,
  eventName,
  renderBlockActions,
  renderInlineNumber,
}: AssistantMessageProps) => {
  const [copied, setCopied] = useState(false);
  const alert = useAlert();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const inlineBlocks = useChatContext()?.inlineBlocks;

  const renderInlineBlock = useCallback(
    (name: string): ReactNode => {
      if (!inlineBlocks) return null;
      const block = resolveInlineBlock(inlineBlocks, name, eventName);
      if (!block) return null;

      return (
        <Box mt={2}>
          {renderBlockActions && (
            <Box display="flex" justifyContent="flex-end" mb={1}>
              {renderBlockActions(block)}
            </Box>
          )}
          {renderBlockContent(block)}
        </Box>
      );
    },
    [inlineBlocks, eventName, renderBlockActions],
  );

  const renderNumber = useCallback(
    (name: string, value: ReactNode): ReactNode => {
      if (!inlineBlocks || !renderInlineNumber) return value;
      const block = resolveInlineNumberBlock(inlineBlocks, name, eventName);
      if (!block) return value;
      return renderInlineNumber(block, value);
    },
    [inlineBlocks, eventName, renderInlineNumber],
  );

  const inlineRenderers = useMemo(
    () => ({ renderBlock: renderInlineBlock, renderNumber }),
    [renderInlineBlock, renderNumber],
  );

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(stripPulseNumberMarkers(content));
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 1000);
    } catch (error) {
      alert.post({
        severity: 'error',
        message: 'Could not copy to clipboard.',
      });
    }
  };

  return (
    <div className={styles.root} data-event-name={eventName}>
      <InlineBlockRenderProvider value={inlineRenderers}>
        <MarkdownContent>{content}</MarkdownContent>
      </InlineBlockRenderProvider>
      <div className={styles.actions}>
        <IconButton
          className={styles.actionButton}
          size="small"
          onClick={handleCopy}
          aria-label="Copy message as Markdown"
        >
          {copied ? <RiCheckLine size={14} /> : <RiFileCopyLine size={14} />}
        </IconButton>
        {eventName && (
          <FeedbackToolbar eventName={eventName} content={content} />
        )}
      </div>
    </div>
  );
};
